import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Camera, Upload, Loader2, ArrowRight, AlertTriangle, CheckCircle2, X, Sparkles } from "lucide-react";

const REFERENCES = [
  { value: "none", label: "Geen referentie", size: null },
  { value: "coin_1eur", label: "Munt 1 € (Ø 23,25 mm)", size: 23.25 },
  { value: "coin_2eur", label: "Munt 2 € (Ø 25,75 mm)", size: 25.75 },
  { value: "credit_card", label: "Bankpas (85,6 mm breed)", size: 85.6 },
  { value: "ruler_cm", label: "Liniaal in cm", size: null },
  { value: "hand", label: "Hand naast pijp", size: null },
];

export default function PipeScanPage() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState("");
  const [reference, setReference] = useState(REFERENCES[0]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Alleen JPG, PNG of WEBP toegestaan.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Bestand te groot (max 8 MB).");
      return;
    }
    setError("");
    setResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setPreview(dataUrl);
      // Strip prefix
      const b64 = dataUrl.split(",")[1] || "";
      setBase64(b64);
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!base64 || loading) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await api.post("/pipe-scan", {
        image_base64: base64,
        reference_object: reference.value === "none" ? null : reference.label,
        reference_size_mm: reference.size,
      });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Analyse mislukt. Probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setBase64("");
    setResult(null);
    setError("");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pipe-scan-page">
      <div className="bg-nibro-sage-pale py-10">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-10">
          <div className="label-uppercase">AI Pipe Scan</div>
          <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl">
            Foto in. Diameter uit.
          </h1>
          <p className="mt-3 max-w-2xl text-zinc-600">
            Upload een foto van je pijp — onze AI schat de buitendiameter (D1 mm),
            converteert naar DN / inch en linkt direct door naar passende beugels.
            Voor maximale nauwkeurigheid: leg een referentie-object (munt of bankpas) naast de pijp.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-5 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Upload */}
          <div className="rounded-md border-2 border-dashed border-nibro-sage bg-nibro-sage-pale/30 p-6">
            <div className="label-uppercase mb-3">1 · Foto uploaden</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="pipe-scan-file-input"
            />
            {preview ? (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="aspect-video w-full rounded-md object-contain bg-white"
                  data-testid="pipe-scan-preview"
                />
                <button
                  type="button"
                  onClick={reset}
                  className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-zinc-700 hover:bg-white"
                  data-testid="pipe-scan-reset"
                  aria-label="Verwijder foto"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-md border border-zinc-200 bg-white text-zinc-500 transition hover:border-nibro-sage hover:bg-nibro-sage-pale"
                data-testid="pipe-scan-upload-button"
              >
                <Upload size={28} className="text-nibro-sage-dark" />
                <div className="text-sm font-semibold">Klik om foto te kiezen</div>
                <div className="font-mono text-[11px] text-zinc-400">JPG · PNG · WEBP · max 8 MB</div>
              </button>
            )}

            <div className="mt-5">
              <div className="label-uppercase mb-2">2 · Referentie-object</div>
              <select
                value={reference.value}
                onChange={(e) =>
                  setReference(REFERENCES.find((r) => r.value === e.target.value) || REFERENCES[0])
                }
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-nibro-sage focus:outline-none"
                data-testid="pipe-scan-reference"
              >
                {REFERENCES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-zinc-500">
                Een referentie-object met bekende afmeting verhoogt de schattings­nauwkeurigheid sterk.
              </p>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={!base64 || loading}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-4 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="pipe-scan-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Analyseren...
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Analyseer foto
                </>
              )}
            </button>

            {error && (
              <div
                className="mt-3 flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="pipe-scan-error"
              >
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Result */}
          <div className="rounded-md border border-zinc-200 bg-white p-6">
            <div className="label-uppercase mb-3">3 · Resultaat</div>
            {!result && !loading && (
              <div className="flex h-64 flex-col items-center justify-center gap-2 text-center text-zinc-400">
                <Camera size={32} />
                <div className="text-sm">Wacht op analyse...</div>
              </div>
            )}
            {loading && (
              <div className="flex h-64 flex-col items-center justify-center gap-3 text-zinc-500">
                <Loader2 size={28} className="animate-spin text-nibro-sage-dark" />
                <div className="text-sm">AI is je foto aan het analyseren...</div>
              </div>
            )}
            {result && (
              <div data-testid="pipe-scan-result">
                <div className="rounded-md border-2 border-nibro-sage bg-nibro-sage-pale p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-nibro-sage-dark">
                    Geschatte buitendiameter
                  </div>
                  <div className="mt-1 font-display text-5xl font-bold text-zinc-900">
                    {result.diameter_mm ? `Ø ${result.diameter_mm} mm` : "—"}
                  </div>
                  <div className="mt-2 flex gap-2">
                    {result.dn && (
                      <span className="rounded-md bg-white px-3 py-1 font-mono text-xs font-bold text-nibro-sage-dark">
                        DN{result.dn}
                      </span>
                    )}
                    {result.inch && (
                      <span className="rounded-md bg-white px-3 py-1 font-mono text-xs font-bold text-nibro-sage-dark">
                        {result.inch}
                      </span>
                    )}
                    <span
                      className={`rounded-md px-3 py-1 font-mono text-xs font-bold uppercase ${
                        result.confidence === "high"
                          ? "bg-emerald-100 text-emerald-800"
                          : result.confidence === "medium"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-zinc-100 text-zinc-700"
                      }`}
                      data-testid="pipe-scan-confidence"
                    >
                      {result.confidence === "high"
                        ? "Hoog vertrouwen"
                        : result.confidence === "medium"
                        ? "Gemiddeld vertrouwen"
                        : "Laag vertrouwen"}
                    </span>
                  </div>
                </div>

                {result.notes && (
                  <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50 p-4 text-xs leading-relaxed text-zinc-700">
                    <div className="font-mono uppercase tracking-wider text-zinc-500">AI uitleg</div>
                    <div className="mt-1">{result.notes}</div>
                  </div>
                )}

                {result.dn && (
                  <Link
                    to={`/dn/${result.dn}`}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-nibro-sage px-4 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
                    data-testid="pipe-scan-to-dn"
                  >
                    Bekijk {result.products_count || ""} beugels voor DN{result.dn} <ArrowRight size={14} />
                  </Link>
                )}

                {result.confidence !== "high" && (
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Schatting is niet 100% betrouwbaar zonder referentie-object. Verifieer altijd met een schuifmaat of meetlint.
                    </span>
                  </div>
                )}
                {result.confidence === "high" && (
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
                    <span>Hoge nauwkeurigheid door duidelijke referentie.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
